<template>
  <v-app>
    <v-main>

      <div class="payment-calculator">

        <hr class="mb-4 mt-0" />
        <v-container fluid class="pa-8">
          <v-row justify="center">
            <v-col cols="12" lg="10" xl="8">
              <!-- Header con botones -->
              <div class="d-flex align-center justify-space-between mb-8">
                <div>
                  <h1 class="display-1 font-weight-bold primary--text mb-1">
                    Calculadora de Cuotas
                  </h1>
                  <p class="subtitle-1 primary--text font-weight-light">
                    Planifica tus pagos mensuales cada 15 del mes
                  </p>
                </div>
                <div class="d-flex">
                  <v-btn color="error" outlined class="mr-3" @click="cancel">
                    <v-icon left>mdi-close</v-icon>
                    Cancelar
                  </v-btn>
                  <v-btn color="primary" :loading="loadingSave" :disabled="!canSave" @click="openConfirmDialog">
                    <v-icon left>mdi-content-save</v-icon>
                    Guardar Plan
                  </v-btn>
                </div>
              </div>

              <v-card class="card-container mb-6" elevation="24" rounded="xl">
                <!-- Loader overlay simple -->
                <v-overlay :value="loading" absolute opacity="0.9" color="white" class="rounded-xl">
                  <div class="text-center">
                    <v-progress-circular indeterminate size="50" width="3" color="primary"></v-progress-circular>
                    <p class="mt-3 text-subtitle-2 primary--text">
                      Cargando datos...
                    </p>
                  </div>
                </v-overlay>
                <!-- Contenido normal (se oculta automáticamente con el overlay) -->

                <v-card-text class="pa-4 pa-md-6 primary--text">
                  <v-row align="center">
                    <v-col cols="12" md="6" class="mb-2 mb-md-0">
                      <div class="d-flex align-center mb-2">
                        <v-icon small color="primary" class="mr-2">mdi-account</v-icon>
                        <p class="mb-0 primary--text">
                          <b>Estudiante:</b> {{ estudiante.nombres }} {{ estudiante.primer_apellido }}
                          {{ estudiante.segundo_apellido }}
                        </p>
                      </div>
                      <div class="d-flex align-center mb-2">
                        <v-icon small color="primary" class="mr-2">mdi-card-account-details</v-icon>
                        <p class="mb-0 primary--text"><b>Documento:</b> {{ estudiante.numero_documento }}</p>
                      </div>
                      <div class="d-flex align-center">
                        <v-icon small color="primary" class="mr-2">mdi-phone</v-icon>
                        <p class="mb-0 primary--text"><b>Teléfono:</b> {{ estudiante.telefono1 }}</p>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="d-flex align-center mb-2">
                        <v-icon small color="primary" class="mr-2">mdi-school</v-icon>
                        <p class="mb-0 primary--text"><b>Programa:</b> {{ programa.nombre_compuesto }}</p>
                      </div>
                      <div class="d-flex align-center">
                        <v-icon small color="primary" class="mr-2">mdi-identifier</v-icon>
                        <p class="mb-0 primary--text"><b>Inscripción:</b> #{{ inscripcion.id }}</p>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>


              <!-- Main Card -->
              <v-card class="card-container mb-6" elevation="24" rounded="xl">
                <v-card-text class="pa-6">
                  <v-row class="mb-2">
                    <!-- Concepto de Pago -->
                    <v-col cols="12" md="6" lg="3">
                      <div class="input-group" style="animation-delay: 0.05s">
                        <label class="field-label d-flex align-center mb-1">
                          <v-icon color="primary" size="16" class="mr-1">mdi-tag</v-icon>
                          CONCEPTO
                        </label>
                        <v-select v-model="concept" :items="paymentConcepts" item-text="label" item-value="value"
                          placeholder="Seleccionar..." outlined dense hide-details class="custom-select">
                          <template v-slot:prepend-inner>
                            <v-icon color="primary" size="20">mdi-tag-outline</v-icon>
                          </template>
                        </v-select>
                      </div>
                    </v-col>

                    <!-- Monto Total -->
                    <v-col cols="12" md="6" lg="3">
                      <div class="input-group" style="animation-delay: 0.1s">
                        <label class="field-label d-flex align-center mb-1">
                          <v-icon color="primary" size="16" class="mr-1">mdi-currency-usd</v-icon>
                          MONTO TOTAL
                        </label>
                        <v-text-field v-model="totalAmount" type="number" placeholder="Ej: 10000" outlined dense
                          hide-details class="custom-input monospace-input">
                          <template v-slot:prepend-inner>
                            <v-icon color="primary" size="20">mdi-cash</v-icon>
                          </template>
                        </v-text-field>
                      </div>
                    </v-col>

                    <!-- Número de Cuotas -->
                    <v-col cols="12" md="6" lg="3">
                      <div class="input-group" style="animation-delay: 0.2s">
                        <label class="field-label d-flex align-center mb-1">
                          <v-icon color="primary" size="16" class="mr-1">mdi-credit-card</v-icon>
                          NÚMERO DE CUOTAS
                        </label>
                        <v-text-field v-model="installments" type="number" min="1" placeholder="Ej: 12" outlined dense
                          hide-details class="custom-input">
                          <template v-slot:prepend-inner>
                            <v-icon color="primary" size="20">mdi-numeric</v-icon>
                          </template>
                        </v-text-field>
                      </div>
                    </v-col>

                    <!-- Fecha de Inicio -->
                    <v-col cols="12" md="6" lg="3">
                      <div class="input-group" style="animation-delay: 0.3s">
                        <label class="field-label d-flex align-center mb-1">
                          <v-icon color="primary" size="16" class="mr-1">mdi-calendar</v-icon>
                          FECHA DE INICIO
                        </label>
                        <v-text-field v-model="startDate" type="date" outlined dense hide-details class="custom-input">
                          <template v-slot:prepend-inner>
                            <v-icon color="primary" size="20">mdi-calendar-month</v-icon>
                          </template>
                        </v-text-field>
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Summary Section -->
                  <div v-if="paymentSchedule.length > 0" class="mt-6">
                    <!-- Concepto Badge -->
                    <v-chip class="mb-3 concept-badge" color="primary" text-color="white" label small>
                      <v-icon left size="16">mdi-tag</v-icon>
                      {{ getConceptLabel }}
                    </v-chip>

                    <v-card class="summary-card" elevation="0">
                      <v-card-text class="pa-4">
                        <v-row class="text-center">
                          <v-col cols="12" md="4" class="py-2">
                            <div class="summary-label">TOTAL A PAGAR</div>
                            <div class="summary-value monospace-font">
                              {{ formatCurrency(parseFloat(totalAmount)) }}
                            </div>
                          </v-col>
                          <v-col cols="12" md="4" class="py-2">
                            <div class="summary-label">CUOTA MENSUAL</div>
                            <div class="summary-value monospace-font">
                              {{ formatCurrency(parseFloat(totalAmount) / parseInt(installments)) }}
                            </div>
                          </v-col>
                          <v-col cols="12" md="4" class="py-2">
                            <div class="summary-label">PLAZO TOTAL</div>
                            <div class="summary-value">{{ installments }} meses</div>
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Payment Schedule -->
              <v-card v-if="paymentSchedule.length > 0" class="card-container mb-8" elevation="24" rounded="xl">
                <v-card-text class="pa-6">
                  <div class="d-flex align-center mb-4">
                    <v-icon color="primary" size="24" class="mr-2">mdi-calendar-check</v-icon>
                    <h2 class="text-h6 font-weight-bold mb-0">
                      Calendario de Pagos
                    </h2>
                    <v-spacer></v-spacer>
                    <span class="caption grey--text">
                      {{ paymentSchedule.length }} cuotas
                    </span>
                  </div>

                  <v-row dense>
                    <v-col v-for="(payment, index) in paymentSchedule" :key="index" cols="12" class="py-1">
                      <v-card class="payment-item" :class="index % 2 === 0 ? 'payment-item-even' : 'payment-item-odd'"
                        :style="{ animationDelay: `${0.1 + index * 0.03}s` }" flat outlined hover>
                        <v-card-text class="pa-3">
                          <v-row align="center" no-gutters>
                            <v-col cols="auto" class="pr-3">
                              <v-avatar size="36" class="payment-number">
                                <span class="white--text font-weight-bold caption">
                                  {{ payment.installmentNumber }}
                                </span>
                              </v-avatar>
                            </v-col>
                            <v-col>
                              <div class="d-flex align-center">
                                <div class="payment-title mr-2">
                                  Cuota {{ payment.installmentNumber }}
                                </div>
                                <v-chip x-small color="grey lighten-3" text-color="grey darken-2" class="ml-2">
                                  {{ payment.formattedDate.split(' ')[0] }}
                                </v-chip>
                              </div>
                              <div class="payment-date d-flex align-center caption">
                                <v-icon x-small class="mr-1">mdi-calendar</v-icon>
                                {{ payment.formattedDate }}
                              </div>
                            </v-col>
                            <v-col cols="auto">
                              <div class="payment-amount monospace-font font-weight-bold">
                                {{ formatCurrency(payment.amount) }}
                              </div>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Empty State -->
              <v-card v-else class="empty-state" elevation="24" rounded="xl">
                <v-card-text class="pa-10 text-center">
                  <v-icon size="56" color="grey lighten-2" class="mb-3">
                    mdi-credit-card-outline
                  </v-icon>
                  <h3 class="text-h6 font-weight-medium mb-2 grey--text text--darken-2">
                    Completa los campos para calcular
                  </h3>
                  <p class="body-2 grey--text mb-0">
                    Selecciona el concepto, ingresa el monto total, número de cuotas y fecha de inicio
                  </p>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

        </v-container>
      </div>

      <!-- Dialog de Confirmación -->
      <v-dialog v-model="confirmDialog" persistent max-width="500">
        <v-card>
          <v-card-title class="headline grey lighten-2">
            <v-icon color="primary" class="mr-2">mdi-alert-circle</v-icon>
            Confirmar Guardado
          </v-card-title>

          <v-card-text class="pa-6">
            <div class="d-flex align-center mb-4">
              <v-icon color="warning" large class="mr-3">mdi-alert</v-icon>
              <span class="text-body-1">
                ¿Está seguro que desea guardar el plan de pagos?
              </span>
            </div>

            <v-alert v-if="paymentSchedule.length > 0" type="info" dense class="mb-4">
              <div class="d-flex align-center">
                <v-icon small class="mr-2">mdi-information</v-icon>
                <div>
                  <strong>Resumen:</strong>
                  {{ paymentSchedule.length }} cuotas de
                  {{ formatCurrency(parseFloat(totalAmount) / parseInt(installments)) }}
                  cada una
                </div>
              </div>
            </v-alert>

            <div v-if="paymentSchedule.length > 0" class="text-caption grey--text">
              Se guardarán {{ paymentSchedule.length }} registros de cuotas
            </div>
          </v-card-text>

          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="confirmDialog = false">
              <v-icon left small>mdi-close</v-icon>
              Cancelar
            </v-btn>
            <v-btn color="primary" :loading="saving" @click="savePlan">
              <v-icon left small>mdi-content-save</v-icon>
              Guardar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar para mensajes -->
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" top>
        <div class="d-flex align-center">
          <v-icon class="mr-2">{{ snackbar.icon }}</v-icon>
          {{ snackbar.message }}
        </div>
        <template v-slot:action="{ attrs }">
          <v-btn icon text v-bind="attrs" @click="snackbar.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
    </v-main>
  </v-app>
</template>

<script>
import xaxios from '@/xaxios';

export default {
  name: 'PaymentCalculator',

  data() {
    return {
      loadingSave: false,
      loading: false,
      estudiante: {},
      programa: {},
      inscripcion: {
        plan_pagos: [],
      },
      concept: '',
      totalAmount: '',
      installments: '',
      startDate: '',
      paymentSchedule: [],
      confirmDialog: false,
      saving: false,
      snackbar: {
        show: false,
        message: '',
        color: '',
        icon: ''
      },
      //inscripcionId: 85650, // Puedes recibir esto por props o de la URL
      paymentConcepts: [
        { value: '378', label: 'TRANSPORTE MAÑANA' },
        { value: '379', label: 'TRANSPORTE TARDE' },
        { value: '233', label: 'IDIOMAS' },

        // Agrega más conceptos según necesites
      ]
    };
  },
  props: {
    idprograma: {
      type: [String, Number], // Puede ser string (de la URL) o number
      required: true
    },
    idpersona: {
      type: [String, Number],
      required: true
    },
    idinscripcion: {
      type: [String, Number],
      required: true
    }
  },

  computed: {
    getConceptLabel() {
      const found = this.paymentConcepts.find(c => c.value === this.concept);
      return found ? found.label : '';
    },

    canSave() {
      return this.concept &&
        this.totalAmount &&
        parseFloat(this.totalAmount) > 0 &&
        this.installments &&
        parseInt(this.installments) > 0 &&
        this.startDate &&
        this.paymentSchedule.length > 0;
    }
  },
  mounted() {
    // Cargar datos iniciales si es necesario
    // window.console.log('Programa:', this.idprograma);
    // window.console.log('Persona:', this.idpersona);
    // window.console.log('Inscripción:', this.idinscripcion);
    // this.cargarPrograma(this.idprograma);
    // this.cargarPersona(this.idpersona);
    // this.cargarInscripcion(this.idinscripcion);
    this.getinscripcionx(this.idpersona, this.idprograma, this.idinscripcion);
  },


  watch: {

    // idprograma(newVal, oldVal) {
    //   if (newVal !== oldVal) {
    //     this.cargarPrograma(newVal);
    //   }
    // },
    // idpersona(newVal, oldVal) {
    //   if (newVal !== oldVal) {
    //     this.cargarPersona(newVal);
    //   }
    // },
    // idinscripcion(newVal, oldVal) {
    //   if (newVal !== oldVal) {
    //     this.cargarInscripcion(newVal);
    //   }
    // },
    concept() {
      this.calculatePayments();
    },
    totalAmount() {
      this.calculatePayments();
    },
    installments() {
      this.calculatePayments();
    },
    startDate() {
      this.calculatePayments();
    }
  },

  methods: {
    getinscripcionx(idpersona, idprograma, idinscripcion) {
      this.inscripcion.id = idinscripcion;
      let unidad_negocio_id = this.$store.state.CARGO.sede.unidad_negocio;
      this.loading = true;
      this.msg = '';
      const vue = this;
      const url1 = `${process.env.VUE_APP_API_REST}/esam/persona/id/${idpersona}`;
      const url2 = `${process.env.VUE_APP_API_REST}/esam/programa/id/${idprograma}`;
      const url3 = `${process.env.VUE_APP_API_REST}/api/esam/inscripcion/id/${idinscripcion}`;
      const url4 = `${process.env.VUE_APP_API_REST}/api/pagos/conceptos/buscar`;
      const url5 = `${process.env.VUE_APP_API_REST}/api/v1/inscripcion/${idinscripcion}/plan-pago/kardex`;
      const promise1 = xaxios.get(url1);
      const promise2 = xaxios.get(url2);
      const promise3 = xaxios.get(url3);
      const promise4 = xaxios.post(url4, { unidad_negocio_id: unidad_negocio_id });
      const promise5 = xaxios.get(url5);
      Promise.all([promise1, promise2, promise3, promise4, promise5])
        .then(function (values) {
          let data1 = values[0].data;
          let data2 = values[1].data;
          let data3 = values[2].data;
          let data4 = values[3].data;
          let data5 = values[4].data;
          data5.data.map((x) => {
            x.mod_retraso = true;
            x.mod_monto = true;
            x.mod_fecha = true;
            x.add_item = true;
            x.del_item = true;
          });
          data3.plan_pagos = data5.data;
          window.console.log('=================', data3, data5);
          data4 = data4.filter((x) => !x.opcional);
          vue.estudiante = data1.persona;
          vue.programa = data2;
          vue.conceptos = data4;
          // vue.ordenar(data3);
          vue.inscripcion = data3;
          //vue.handlerPagos();
          vue.loading = false;
          //window.console.log(JSON.stringify(data3.plan_pagos, null, 4));
        })
        .catch((e) => {
          window.console.error(e);
          vue.loading = false;
        });
    },

    cargarInscripcion(idinscripcion) {
      // Lógica para cargar datos de la inscripción
      window.console.log('Cargando inscripción con ID:', idinscripcion);
      // Aquí puedes hacer una llamada a la API para obtener los datos
    },
    cargarPrograma(idprograma) {
      // Lógica para cargar datos del programa
      window.console.log('Cargando programa con ID:', idprograma);
      // Aquí puedes hacer una llamada a la API para obtener los datos
    },
    cargarPersona(idpersona) {
      // Lógica para cargar datos de la persona
      window.console.log('Cargando persona con ID:', idpersona);
      // Aquí puedes hacer una llamada a la API para obtener los datos
    },
    calculatePayments() {
      if (!this.concept || !this.totalAmount || !this.installments || !this.startDate) {
        this.paymentSchedule = [];
        return;
      }

      const total = parseFloat(this.totalAmount);
      const numInstallments = parseInt(this.installments);

      // Parsear la fecha manualmente para evitar problemas de zona horaria
      const [year, month, day] = this.startDate.split('-').map(Number);
      const start = new Date(year, month - 1, day, 12, 0, 0); // Mediodía para evitar problemas

      const amountPerInstallment = total / numInstallments;
      const schedule = [];

      for (let i = 0; i < numInstallments; i++) {
        const paymentDate = new Date(start);
        paymentDate.setMonth(start.getMonth() + i);
        // paymentDate.setDate(15);
        paymentDate.setHours(12, 0, 0, 0);

        // Formatear fecha en formato YYYY-MM-DD para el JSON
        const formattedDate = paymentDate.toISOString().split('T')[0];

        schedule.push({
          installmentNumber: i + 1,
          date: paymentDate,
          amount: amountPerInstallment,
          formattedDate: paymentDate.toLocaleDateString('es-ES', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
          }),
          isoDate: formattedDate
        });
      }

      this.paymentSchedule = schedule;
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat('es-BO', {
        style: 'currency',
        currency: 'BOB',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    },

    formatDateForAPI(date) {
      // Convierte Date a formato YYYY-MM-DD
      return date.toISOString().split('T')[0];
    },

    cancel() {
      // Navegar a la ruta anterior
      this.$router.go(-1);
    },

    openConfirmDialog() {
      if (!this.canSave) {
        this.showSnackbar('Por favor complete todos los campos', 'warning', 'mdi-alert');
        return;
      }
      this.confirmDialog = true;
    },

    async savePlan() {
      this.saving = true;
      this.loadingSave = true;

      try {
        // Preparar el JSON según el ejemplo
        const planPagos = this.paymentSchedule.map(payment => ({
          numero_cuota: payment.installmentNumber,
          concepto_pago_id: parseInt(this.concept),
          monto: payment.amount,
          fecha_pago: payment.isoDate
        }));

        const requestData = {
          inscripcion_id: this.idinscripcion,
          plan_pagos: planPagos
        };

        window.console.log('Enviando datos:', JSON.stringify(requestData, null, 2));

        // Aquí iría la llamada real a la API
        // Ejemplo con axios:
        // const response = await this.$axios.post('/api/v1/inscripcion/plan-pago', requestData);

        // Simulación de éxito
        //await new Promise(resolve => setTimeout(resolve, 1500));

        let url = `${process.env.VUE_APP_API_REST}/api/v1/inscripcion/add-concepto`;
        xaxios
          .put(url, requestData)
          .then((res) => {
            window.console.log(res.data);
            if (typeof res.data.data == 'string') {
              this.showSnackbar('Plan de pagos guardado exitosamente', 'success', 'mdi-check-circle');

              setTimeout(() => {
                this.$router.go(-1);
              }, 2000);
            }

            //vue.getdefault();
          })
          .catch((error) => {

            this.showSnackbar('Error al guardar el plan de pagos', error.response.data.data, 'mdi-alert-circle');

            // try {
            //   vue.msg = error.response.data.data;
            // } catch (e) {
            //   window.console.error(e);
            // }
            // if (error.response.data.error)
            //   vue.msg = error.response.data.error;

            // vue.loadingediting = false;

          });

        // Cerrar diálogo
        this.confirmDialog = false;

        // Mostrar mensaje de éxito

        // Redirigir después de un momento
        // setTimeout(() => {
        //   this.$router.go(-1);
        // }, 2000);

      } catch (error) {
        window.console.error('Error al guardar:', error);
        this.showSnackbar('Error al guardar el plan de pagos', 'error', 'mdi-alert-circle');
      } finally {
        this.saving = false;
      }
    },

    showSnackbar(message, color = 'info', icon = 'mdi-information') {
      this.snackbar = {
        show: true,
        message,
        color,
        icon
      };
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;700&family=Space+Mono:wght@400;700&display=swap');

.payment-calculator {
  min-height: 100vh;
  font-family: 'Outfit', -apple-system, system-ui, sans-serif;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.fade-in-up {
  animation: fadeInUp 0.5s ease-out;
}

.card-container {
  animation: fadeInUp 0.6s ease-out;
}

.input-group {
  animation: slideIn 0.5s ease-out;
}

.payment-item {
  animation: slideIn 0.3s ease-out backwards;
  transition: all 0.2s ease;
  border-radius: 8px !important;
  cursor: pointer;
}

.payment-item:hover {
  border-color: #105a97 !important;
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1) !important;
}

.payment-item-even {
  background-color: #f7fafc;
}

.payment-item-odd {
  background-color: white;
}

/* Field Styling */
.field-label {
  font-size: 12px;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.custom-input>>>.v-input__control,
.custom-select>>>.v-input__control {
  min-height: 40px !important;
}

.custom-input>>>.v-input__slot,
.custom-select>>>.v-input__slot {
  min-height: 40px !important;
  padding: 0 12px !important;
}

.custom-input>>>.v-text-field__details,
.custom-select>>>.v-text-field__details {
  display: none;
}

.custom-input>>>fieldset,
.custom-select>>>fieldset {
  border-width: 1.5px;
  border-color: #e2e8f0;
  transition: all 0.3s ease;
}

.custom-input>>>.v-input--is-focused fieldset,
.custom-select>>>.v-input--is-focused fieldset {
  border-color: #105a97 !important;
}

.monospace-input>>>input {
  font-family: 'Space Mono', monospace !important;
  font-weight: 600;
  font-size: 16px;
}

/* Concept Badge */
.concept-badge {
  background: linear-gradient(135deg, #458ee2 0%, #105a97 100%) !important;
  border-radius: 16px !important;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
  animation: fadeInUp 0.4s ease-out;
}

/* Summary Card */
.summary-card {
  background: linear-gradient(135deg, #458ee2 0%, #105a97 100%) !important;
  border-radius: 12px !important;
  animation: fadeInUp 0.5s ease-out;
}

.summary-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 2px;
  font-weight: 300;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.summary-value {
  font-size: 20px;
  color: white;
  font-weight: 700;
  line-height: 1.2;
}

.monospace-font {
  font-family: 'Space Mono', monospace !important;
}

/* Payment Item - Compact */
.payment-number {
  background: linear-gradient(135deg, #458ee2 0%, #105a97 100%) !important;
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.2);
  border-radius: 8px !important;
}

.payment-title {
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
}

.payment-date {
  font-size: 11px;
  color: #718096;
  margin-top: 2px;
}

.payment-amount {
  font-size: 16px;
  color: #105a97;
  white-space: nowrap;
}

/* Responsive */
@media (max-width: 960px) {
  .summary-value {
    font-size: 18px;
  }

  .payment-amount {
    font-size: 14px;
  }

  .payment-title {
    font-size: 13px;
  }

  .payment-date {
    font-size: 10px;
  }

  .d-flex.align-center.justify-space-between.mb-8 {
    flex-direction: column;
    align-items: flex-start !important;
  }

  .d-flex.mb-8>div:last-child {
    margin-top: 16px;
    width: 100%;
  }
}

@media (max-width: 600px) {
  .payment-item .v-card-text {
    padding: 12px !important;
  }

  .payment-number {
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
  }

  .payment-number span {
    font-size: 11px;
  }
}
</style>